import { ChevronLeft, X } from "lucide-react";
import { cn } from "../lib/cn";
import { HapticSurface } from "../lib/haptics";

const headingButton =
  "relative grid size-6 shrink-0 place-items-center rounded-lg text-muted-foreground transition duration-200 hover:bg-foreground/10 hover:text-foreground active:scale-95";

/**
 * The line across the top of a panel: what you are looking at, the way back
 * out of it, and the way to dismiss it. Every pane wears one.
 */
export function PanelHeading({
  label,
  onBack,
  onClose,
  centered = false,
  children,
}: {
  /** The pane's name. */
  label?: string;
  /** Omitted on a panel's first pane, which has nothing behind it. */
  onBack?: () => void;
  onClose?: () => void;
  /** The name in the middle between back and close, whether or not either is there. */
  centered?: boolean;
  /** Stands in for the name where the name is itself a control. */
  children?: React.ReactNode;
}) {
  if (centered) {
    return (
      <div className="grid grid-cols-[1.5rem_1fr_1.5rem] items-center gap-2 pb-2">
        {onBack ? (
          <button type="button" aria-label="Back" onClick={onBack} className={headingButton}>
            <ChevronLeft className="size-4" />
            <HapticSurface />
          </button>
        ) : (
          <span />
        )}
        <div className="flex min-w-0 justify-center">
          {children ?? <span className="truncate text-center font-medium">{label}</span>}
        </div>
        {onClose ? (
          <button type="button" aria-label="Close" onClick={onClose} className={headingButton}>
            <X className="size-3.5" />
            <HapticSurface />
          </button>
        ) : (
          <span />
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 pb-2">
      {onBack && (
        <button type="button" aria-label="Back" onClick={onBack} className={headingButton}>
          <ChevronLeft className="size-4" />
          <HapticSurface />
        </button>
      )}

      <div className="flex min-w-0 flex-1 items-center">
        {children ?? (
          // Nudged clear of the edge, which a control does not need because
          // its own padding already holds it there.
          <span className={cn("truncate text-xs text-muted-foreground", !onBack && "pl-2")}>
            {label}
          </span>
        )}
      </div>

      {onClose && (
        <button type="button" aria-label="Close" onClick={onClose} className={headingButton}>
          <X className="size-3.5" />
          <HapticSurface />
        </button>
      )}
    </div>
  );
}
